import { useCallback } from 'react'
import _ from 'lodash'
import uuid from 'uuid'

import { withZero, getLetter, convertIngredientDescription } from '../../../../../common/utils'
import { newStep } from '../../../../../common/step'
import { newAppliance } from '../../../../../common/appliance'

const defaultIngredient = ({ ingredientID, servings, availableUnits }) => ({
  ingredientID,
  newIngredientKey: uuid(),
  servings: servings.reduce((acc, serving) => ({ ...acc, [serving]:{
    quantity: 0,
    unitID: availableUnits[0].id,
  } }), {}),
})

const prepareAndGetAppliances = ({ model, variationField, appliances, defaultLocale }) => {
  let appliancesId = Reflect.ownKeys(
    _.get(model, `values[${variationField}].applianceSteps`, {}),
  )
  if (!appliancesId.length) {
    _.set(model, `values[${variationField}].applianceSteps`, {
      [appliances[0].id]: newAppliance(defaultLocale),
    })
    appliancesId = [appliances[0].id]
  }
  return appliancesId
}

export default ({
  onChange,
  model,
  servings,
  field: variationField,
  availableUnits,
  defaultLocale, showAddIngredient, setShowAddIngredient,
  selectIngredient,
  appliances,
  addToIngredientsList,
}) => {

  const handleAddIngredientModal = useCallback((category, item, index) => {
    setShowAddIngredient({
      category, item, index,
      ingredients: item ?
        [item, ...(item.replacements || [])] : category.ingredients,
    })
  }, [setShowAddIngredient])

  const handleAddIngredient = useCallback(ingredientsList => {
    const ingredientsIDs = ingredientsList.map(i => i.id)
    const originalIngredientsByID = ingredientsList.reduce((acc, value) => ({ ...acc, [value.id]: value }), {})
    const { category, item, index: itemIndex } = showAddIngredient
    const index = model.values[variationField].sections.findIndex(c => c === category)
    let newIngredients = []
    let targetField
    let selectTarget
    let ingredientsToAdd = ingredientsIDs.map(ingredientID => defaultIngredient({
      ingredientID,
      servings,
      availableUnits,
    }))

    if (item) {
      newIngredients = [...(item.replacements || []), ...ingredientsToAdd]
      targetField = `values[${variationField}].sections[${index}].ingredients[${itemIndex}].replacements`
      selectTarget = `sections[${index}].ingredients[${itemIndex}].replacements[${Math.max(0, newIngredients.length - 1)}]`
    } else {
      newIngredients = [...(category.ingredients || []), ...ingredientsToAdd]
      targetField = `values[${variationField}].sections[${index}].ingredients`
      selectTarget = `sections[${index}].ingredients[${Math.max(0, newIngredients.length - 1)}]`
    }
    onChange({
      target: {
        value: newIngredients,
        name: targetField,
      },
    }, model => {
      // add preparation steps
      if (!item) {
        const appliancesIds = prepareAndGetAppliances({ model, variationField, appliances, defaultLocale })
        appliancesIds.forEach(applianceId => {
          const existingSteps = _.get(model, `values[${variationField}].applianceSteps.${applianceId}.preparationSteps`, [])
          let added = false
          ingredientsToAdd.forEach((ingredient, addIndex) => {
            const index = existingSteps.length + 1
            existingSteps.push(newStep({
              defaultLocale,
              title: `Step ${withZero(index)}`,
              type: 'preparation',
              newIngredientKey: ingredient.newIngredientKey,
              recipeIngredientID: ingredient.recipeIngredientID,
              video: originalIngredientsByID[ingredient.ingredientID].image,
              description: convertIngredientDescription(originalIngredientsByID[ingredient.ingredientID]),
            }))
            added = true
          })
          if(added) {
            if (!_.get(model, `values[${variationField}].applianceSteps`)) {
              _.set(model, `values[${variationField}].applianceSteps`, {})
            }
            _.set(model, `values[${variationField}].applianceSteps.${applianceId}.preparationSteps`, existingSteps)
          }
        })
      } else { //add replacement to steps
        const appliancesId = Reflect.ownKeys(
          _.get(model, `values[${variationField}].applianceSteps`, { [appliances[0].id]: 'new' }),
        )
        appliancesId.forEach(applianceId => {
          const preparationSteps = _.get(model, `values[${variationField}].applianceSteps.${applianceId}.preparationSteps`, [])
          preparationSteps.forEach((step, stepIndex) => {
            if ((step.newIngredientKey && step.newIngredientKey === item.newIngredientKey) ||
            (step.recipeIngredientID && step.recipeIngredientID === item.recipeIngredientID)) {
              const replacements = step.replacements || []
              const errorStartIndex = replacements.length
              ingredientsToAdd.forEach((ingredient, addIndex) => {
                replacements.push(newStep({
                  defaultLocale,
                  type: 'preparation',
                  title: `${step.title[defaultLocale]} ${getLetter(errorStartIndex + addIndex)}`,
                  newIngredientKey: ingredient.newIngredientKey,
                  recipeIngredientID: ingredient.recipeIngredientID,
                  video: originalIngredientsByID[ingredient.ingredientID].image,
                  description: convertIngredientDescription(originalIngredientsByID[ingredient.ingredientID]),
                }))
                step.replacements = replacements
              })
            }
          })
        })
      }

      return model
    })
    addToIngredientsList(ingredientsList)
    selectIngredient(selectTarget)
    setShowAddIngredient(null)
  }, [
    appliances, availableUnits, defaultLocale, model.values, onChange, selectIngredient, servings,
    setShowAddIngredient, showAddIngredient, variationField, addToIngredientsList,
  ])

  return { handleAddIngredientModal, handleAddIngredient }
}
